import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UseMutateFunction } from '@tanstack/react-query';
import queryString from 'query-string';
import { toast } from '@crono-one/design-system/side-effects';

import CronoError from 'crono-fe-common/types/error';
import { useAuth } from 'context/auth';

type OauthCallbackParams = ReturnType<typeof queryString.parse>;

type ExchangeOauthCode<TData, TVariables> = UseMutateFunction<
  TData,
  CronoError,
  TVariables
>;

export interface UseOauthCallbackCodeOptions<TData, TVariables> {
  /** Provider name used in the missing-code toast, e.g. `Aircall`. */
  provider: string;
  exchangeCode: ExchangeOauthCode<TData, TVariables>;
  buildVariables: (code: string) => TVariables;
  /** Destination for both ways of exiting before the exchange: no code, and an aborted `guard`. */
  missingCodePath: string;
  successPath: string;
  errorPath: string;
  /**
   * Extra validation run once the code is known to be present — return false to
   * abort the exchange. Emitting the user feedback is the guard's job; the hook
   * still runs `cleanUp` and leaves for `missingCodePath`.
   */
  guard?: (params: OauthCallbackParams) => boolean;
  /**
   * Runs on all four exits: no code, an aborted guard, and both mutation
   * outcomes. Cleanup that must survive a failed exchange belongs in
   * `onSuccess`/`onError` instead.
   */
  cleanUp?: () => void;
  /**
   * Awaited before the route is left, so whatever the callback needs in place —
   * a refreshed user, above all — is already there when the destination renders.
   */
  onSuccess?: () => void | Promise<unknown>;
  onError?: (error: CronoError) => void;
  /**
   * Whether to wait for a logged-in user before exchanging. True for the
   * providers whose exchange endpoint resolves the account from the session.
   */
  requiresUser?: boolean;
}

/**
 * Drives an OAuth callback route: hands the provider's code to `exchangeCode`
 * exactly once — the codes are single-use, so replaying the same redirect would
 * always fail — and leaves the route on every outcome, since the callback pages
 * render nothing but a spinner. A refused consent comes back as
 * `?error=access_denied` with no code and never reaches the API.
 *
 * Navigation always replaces, so pressing Back cannot remount the page and
 * re-post a consumed code.
 */
const useOauthCallbackCode = <TData, TVariables>(
  options: UseOauthCallbackCodeOptions<TData, TVariables>,
) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isRedirectHandled = useRef(false);

  // Every option is read off this ref at use time, never captured when the
  // fire-once effect runs: `useAnalytics()` is still null until Amplitude's
  // async init resolves, which can land after the exchange settles.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    const requiresUser = optionsRef.current.requiresUser ?? true;

    if ((requiresUser && !user) || isRedirectHandled.current) return;
    isRedirectHandled.current = true;

    const leaveCallbackRoute = (path: string) => {
      optionsRef.current.cleanUp?.();
      navigate(path, { replace: true });
    };

    const params = queryString.parse(location.search);
    const { code, error_description: errorDescription } = params;

    if (typeof code !== 'string' || code === '') {
      const { provider } = optionsRef.current;
      toast.error(
        typeof errorDescription === 'string' && errorDescription !== ''
          ? `${provider} authorization failed: ${errorDescription}`
          : `${provider} did not return an authorization code`,
      );
      leaveCallbackRoute(optionsRef.current.missingCodePath);
      return;
    }

    if (optionsRef.current.guard?.(params) === false) {
      leaveCallbackRoute(optionsRef.current.missingCodePath);
      return;
    }

    optionsRef.current.exchangeCode(optionsRef.current.buildVariables(code), {
      onSuccess: async () => {
        try {
          await optionsRef.current.onSuccess?.();
        } catch {
          // The exchange itself succeeded, so a failing refresh is not a reason
          // to strand the user on a route that renders nothing but a spinner.
        }
        leaveCallbackRoute(optionsRef.current.successPath);
      },
      onError: (error) => {
        optionsRef.current.onError?.(error);
        leaveCallbackRoute(optionsRef.current.errorPath);
      },
    });
  }, [user, location.search, navigate]);
};

export default useOauthCallbackCode;
